/* eslint-disable */
import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Clock, RefreshCw, Loader2, MailQuestion, Copy, Send, CalendarClock, CheckCircle2, Inbox } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { API } from "@/lib/constants";

function plusDaysISO(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function ReviewCard({ r, stuck, onDecided }) {
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState(null); // null | 'leave' | 'close' | 'escalate' | 'approach' | 'dead'
  const [leaveDate, setLeaveDate] = useState(plusDaysISO(14));
  const [reason, setReason] = useState("");
  const [escalateTo, setEscalateTo] = useState("");
  const [draft, setDraft] = useState(null);

  const post = useCallback(async (url, body, closeToast) => {
    setBusy(true);
    try {
      const resp = await fetch(url, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const d = await resp.json().catch(() => ({}));
      if (!resp.ok) { toast.error(d.detail || "Could not save the decision"); setBusy(false); return null; }
      setBusy(false); setMode(null);
      if (closeToast) toast.success(closeToast);
      return d;
    } catch (e) { toast.error("Network error"); setBusy(false); return null; }
  }, []);

  const decideStuck = useCallback(async (decision, extra = {}) => {
    const d = await post(`${API}/awaiting-replies/${r.client_id}/stuck-decision`,
      { decision, kind: r.kind || "reply", ...extra });
    if (!d) return;
    const msg = decision === "accept_dead" ? "Accepted dead — recorded and closed."
      : decision === "escalate" ? "Escalated — new review set."
      : "Approach changed — fresh review set.";
    toast.success(msg);
    onDecided(r.client_id, d.active);
  }, [post, r, onDecided]);

  const decide = useCallback(async (decision, extra = {}) => {
    const d = await post(`${API}/awaiting-replies/${r.client_id}/decision`,
      { decision, kind: r.kind || "reply", ...extra });
    if (!d) return;
    if (decision === "chase" && d.draft) {
      setDraft(d.draft);
      const outcome = d.send_outcome ? ` Chase ${d.send_outcome}.` : "";
      toast.success(`New review set for ${d.review_date}.${outcome} A draft is ready.`);
    } else {
      toast.success(decision === "close" ? "Closed out — off the waiting list." : `New review set for ${d.review_date}.`);
      onDecided(r.client_id, decision !== "close");
    }
  }, [post, r, onDecided]);

  const dw = r.days_waiting;
  const waited = dw != null ? `${dw} day${dw === 1 ? "" : "s"}` : "a while";

  return (
    <Card className="border-slate-200" data-testid={`awaiting-review-${r.client_id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-[240px]">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-semibold text-slate-900">{r.company_name}</h3>
              {r.kind === "payment"
                ? <Badge className="bg-rose-100 text-rose-800 border-rose-200 text-[10px]" data-testid={`awaiting-amount-${r.client_id}`}>Payment {r.amount_label || ""}</Badge>
                : <Badge className="bg-sky-100 text-sky-800 border-sky-200 text-[10px]">Reply</Badge>}
              {r.overdue
                ? <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px]">Review due {r.review_date}</Badge>
                : <Badge className="bg-slate-100 text-slate-600 border-slate-200 text-[10px]">Review {r.review_date}</Badge>}
              {r.escalated_to && <Badge className="bg-violet-100 text-violet-800 border-violet-200 text-[10px]">Escalated → {r.escalated_to}</Badge>}
            {r.chase_count > 0 && <Badge variant="outline" className="text-[10px]">Chased {r.chase_count}×</Badge>}
            </div>
            <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
              <Clock className="h-3 w-3" /> Waiting {waited}
              {r.note ? <span className="text-slate-600">· “{r.note}”</span> : (r.reason ? <span className="text-slate-600">· {r.reason}</span> : null)}
            </p>
            <p className="text-[11px] text-slate-400 mt-0.5">Set by {r.set_by ? r.set_by.split("@")[0] : "—"}</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {stuck ? (
              <>
                {r.chase_count >= 3 && <Badge className="bg-orange-100 text-orange-800 border-orange-200 text-[10px]">Chased {r.chase_count}× · no reply</Badge>}
                <Button size="sm" variant="outline" disabled={busy}
                  onClick={() => setMode(mode === "approach" ? null : "approach")} data-testid={`stuck-approach-${r.client_id}`}>
                  Change approach
                </Button>
                <Button size="sm" variant="outline" disabled={busy}
                  onClick={() => setMode(mode === "escalate" ? null : "escalate")} data-testid={`stuck-escalate-${r.client_id}`}>
                  Escalate
                </Button>
                <Button size="sm" variant="outline" className="text-rose-600 border-rose-200 hover:bg-rose-50" disabled={busy}
                  onClick={() => setMode(mode === "dead" ? null : "dead")} data-testid={`stuck-dead-${r.client_id}`}>
                  Accept it's dead
                </Button>
              </>
            ) : (
              <>
                <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" disabled={busy}
                  onClick={() => decide("chase", { new_date: plusDaysISO(7) })} data-testid={`awaiting-chase-${r.client_id}`}>
                  {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />} Chase
                </Button>
                <Button size="sm" variant="outline" disabled={busy}
                  onClick={() => setMode(mode === "leave" ? null : "leave")} data-testid={`awaiting-leave-${r.client_id}`}>
                  <CalendarClock className="h-4 w-4 mr-1" /> Leave longer
                </Button>
                <Button size="sm" variant="outline" className="text-rose-600 border-rose-200 hover:bg-rose-50" disabled={busy}
                  onClick={() => setMode(mode === "close" ? null : "close")} data-testid={`awaiting-close-${r.client_id}`}>
                  <CheckCircle2 className="h-4 w-4 mr-1" /> Close out
                </Button>
              </>
            )}
          </div>
        </div>

        {mode === "leave" && (
          <div className="mt-3 flex items-end gap-2 bg-slate-50 rounded-md p-3">
            <div>
              <label className="text-[10px] uppercase tracking-wide text-slate-500">New review date</label>
              <input type="date" value={leaveDate} onChange={(e) => setLeaveDate(e.target.value)}
                className="block mt-1 h-9 px-2 text-sm border border-slate-300 rounded" data-testid={`awaiting-leave-date-${r.client_id}`} />
            </div>
            <Button size="sm" disabled={busy || !leaveDate} onClick={() => decide("leave", { new_date: leaveDate })}
              data-testid={`awaiting-leave-confirm-${r.client_id}`}>Set new date</Button>
          </div>
        )}

        {mode === "close" && (
          <div className="mt-3 bg-slate-50 rounded-md p-3">
            <label className="text-[10px] uppercase tracking-wide text-slate-500">Why are you closing this out?</label>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
              className="w-full mt-1 text-sm border border-slate-300 rounded p-2" placeholder="e.g. Client replied and declined / no longer relevant"
              data-testid={`awaiting-close-reason-${r.client_id}`} />
            <div className="flex justify-end mt-2">
              <Button size="sm" className="bg-rose-600 hover:bg-rose-700" disabled={busy}
                onClick={() => decide("close", { reason })} data-testid={`awaiting-close-confirm-${r.client_id}`}>Close it out</Button>
            </div>
          </div>
        )}
        {mode === "approach" && (
          <div className="mt-3 bg-slate-50 rounded-md p-3">
            <label className="text-[10px] uppercase tracking-wide text-slate-500">New approach — what will you try instead?</label>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
              className="w-full mt-1 text-sm border border-slate-300 rounded p-2" placeholder="e.g. Try a call instead of email; different value angle"
              data-testid={`stuck-approach-reason-${r.client_id}`} />
            <div className="flex justify-end mt-2">
              <Button size="sm" disabled={busy} onClick={() => decideStuck("change_approach", { reason, new_date: plusDaysISO(10) })}
                data-testid={`stuck-approach-confirm-${r.client_id}`}>Set fresh approach</Button>
            </div>
          </div>
        )}

        {mode === "escalate" && (
          <div className="mt-3 bg-slate-50 rounded-md p-3 space-y-2">
            <div>
              <label className="text-[10px] uppercase tracking-wide text-slate-500">Escalate to whom (someone else in that organisation)?</label>
              <input type="text" value={escalateTo} onChange={(e) => setEscalateTo(e.target.value)}
                className="w-full mt-1 h-9 px-2 text-sm border border-slate-300 rounded" placeholder="e.g. their CFO / procurement lead"
                data-testid={`stuck-escalate-to-${r.client_id}`} />
            </div>
            <div className="flex justify-end">
              <Button size="sm" disabled={busy || !escalateTo.trim()}
                onClick={() => decideStuck("escalate", { escalate_to: escalateTo, reason, new_date: plusDaysISO(7) })}
                data-testid={`stuck-escalate-confirm-${r.client_id}`}>Escalate</Button>
            </div>
          </div>
        )}

        {mode === "dead" && (
          <div className="mt-3 bg-slate-50 rounded-md p-3">
            <label className="text-[10px] uppercase tracking-wide text-slate-500">Accept it's dead — record why</label>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
              className="w-full mt-1 text-sm border border-slate-300 rounded p-2" placeholder="e.g. No traction after 3 attempts; deprioritised on their side"
              data-testid={`stuck-dead-reason-${r.client_id}`} />
            <div className="flex justify-end mt-2">
              <Button size="sm" className="bg-rose-600 hover:bg-rose-700" disabled={busy}
                onClick={() => decideStuck("accept_dead", { reason })} data-testid={`stuck-dead-confirm-${r.client_id}`}>Record & close</Button>
            </div>
          </div>
        )}
      </CardContent>

      {/* Drafted follow-up offered after Chase */}
      <Dialog open={!!draft} onOpenChange={(v) => { if (!v) { setDraft(null); onDecided(r.client_id, true); } }}>
        <DialogContent className="max-w-lg" data-testid="awaiting-draft-dialog">
          <DialogHeader>
            <DialogTitle className="text-base flex items-center gap-2"><MailQuestion className="h-5 w-5 text-indigo-600" /> Drafted follow-up</DialogTitle>
          </DialogHeader>
          {draft && (
            <div className="space-y-2 text-sm">
              <p className="text-xs text-slate-500">A new review date is set. Copy this into your email to {r.company_name} — tweak as you like.</p>
              <div>
                <label className="text-[10px] uppercase tracking-wide text-slate-500">Subject</label>
                <div className="mt-1 p-2 bg-slate-50 border rounded text-slate-800">{draft.subject}</div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wide text-slate-500">Body</label>
                <div className="mt-1 p-2 bg-slate-50 border rounded text-slate-800 whitespace-pre-wrap">{draft.body}</div>
              </div>
              {draft.generated_by === "template" && <p className="text-[11px] text-amber-600">Generated from a template (AI drafting unavailable) — please personalise before sending.</p>}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => { navigator.clipboard?.writeText(`${draft?.subject}\n\n${draft?.body}`); toast.success("Copied"); }} data-testid="awaiting-draft-copy">
              <Copy className="h-4 w-4 mr-1" /> Copy
            </Button>
            <Button size="sm" onClick={() => { setDraft(null); onDecided(r.client_id, true); }} data-testid="awaiting-draft-done">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export default function AwaitingReplies({ user }) {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mine, setMine] = useState(false);
  const [overdueOnly, setOverdueOnly] = useState(true);
  const [stuck, setStuck] = useState(() => new URLSearchParams(window.location.search).get("stuck") === "1");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ mine: String(mine), overdue_only: String(overdueOnly), stuck: String(stuck) });
      const r = await fetch(`${API}/awaiting-replies?${params.toString()}`, { credentials: "include" });
      const d = await r.json().catch(() => ({ reviews: [] }));
      setReviews(d.reviews || []);
    } catch (e) {
      toast.error("Couldn't load awaiting replies");
    } finally {
      setLoading(false);
    }
  }, [mine, overdueOnly, stuck]);

  useEffect(() => { load(); }, [load]);

  const onDecided = useCallback((clientId, stillActive) => {
    // Close-out (stillActive=false) leaves the list. Chase/Leave push the date
    // into the future — refetch so the row reappears with its new date when the
    // 'Include upcoming' view is on, and drops off the overdue-only view.
    if (stillActive) load();
    else setReviews((prev) => prev.filter((x) => x.client_id !== clientId));
  }, [load]);

  return (
    <div className="p-4 sm:p-6 max-w-[1100px] mx-auto" data-testid="awaiting-page">
      <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <MailQuestion className="h-7 w-7 text-indigo-600" />
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">Awaiting Replies</h1>
            <p className="text-sm text-slate-500">{stuck
              ? "Chased 3+ times with no reply — is the relationship real? Change approach, escalate, or accept it's dead."
              : "Clients whose review date has passed. Each needs a decision — chase, leave longer, or close out."}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex bg-slate-100 rounded-md p-0.5 text-xs">
            <button onClick={() => setStuck(false)} data-testid="awaiting-filter-active"
              className={`px-3 py-1.5 rounded ${!stuck ? "bg-white shadow-sm font-medium text-slate-900" : "text-slate-600"}`}>Needs a decision</button>
            <button onClick={() => setStuck(true)} data-testid="awaiting-filter-stuck"
              className={`px-3 py-1.5 rounded ${stuck ? "bg-white shadow-sm font-medium text-slate-900" : "text-slate-600"}`}>Stuck 3+</button>
          </div>
          <div className="flex bg-slate-100 rounded-md p-0.5 text-xs">
            <button onClick={() => setMine(false)} data-testid="awaiting-filter-all"
              className={`px-3 py-1.5 rounded ${!mine ? "bg-white shadow-sm font-medium text-slate-900" : "text-slate-600"}`}>Everyone</button>
            <button onClick={() => setMine(true)} data-testid="awaiting-filter-mine"
              className={`px-3 py-1.5 rounded ${mine ? "bg-white shadow-sm font-medium text-slate-900" : "text-slate-600"}`}>Mine</button>
          </div>
          {!stuck && (
          <label className="flex items-center gap-1.5 text-[11px] text-slate-600 cursor-pointer">
            <input type="checkbox" checked={!overdueOnly} onChange={(e) => setOverdueOnly(!e.target.checked)} data-testid="awaiting-show-upcoming" />
            Include upcoming
          </label>
          )}
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="awaiting-refresh">
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      {loading && reviews.length === 0 ? (
        <div className="flex items-center justify-center h-64 text-slate-400"><Loader2 className="animate-spin h-6 w-6" /></div>
      ) : reviews.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-64 text-slate-400" data-testid="awaiting-empty">
          <Inbox className="h-10 w-10 mb-2" />
          <p className="text-sm">Nothing needs a decision right now.</p>
          <p className="text-xs">Clients resurface here when a review date passes.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="awaiting-list">
          {reviews.map((r) => <ReviewCard key={r.client_id} r={r} stuck={stuck} onDecided={onDecided} />)}
        </div>
      )}
    </div>
  );
}
